import React, { useState, useRef, useEffect } from "react";

import JSZip from "jszip";

import FileUploadIcon from "@mui/icons-material/FileUpload";
import HomeIcon from "@mui/icons-material/Home";
import LayersIcon from "@mui/icons-material/Layers";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import MapIcon from "@mui/icons-material/Map";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Feature from "ol/Feature";
import { circular } from "ol/geom/Polygon";
import { Style } from "ol/style";

import MapFullScreen from "../map/mapFullScreen";
import MapInfoCard from "../map/mapInfoCard";
import MapTileDropdown from "../map/mapTileDropdown";

import { MapSettingsDropdown } from "../map/mapSettingsDropdown";
import { MapLayersDropdown } from "../map/mapLayersDropdown";

import {
    createZeroConflictionLineFeature,
    getAsdFeatureStyleFromLayersAndSettings,
    GetFeature,
    GetFeatures,
    getGeometryStyleFromColor,
    getGeometryStyleFromFeatureAndSettings,
    GetLaancAirspaceStyle,
    getLabelTextFromFeatureAndUser,
    GetLayer,
    getPredLineCoordinatesFromTrackAndSettings,
    getZeroConflictionLineCoordinates,
    getZeroConflictionLineStyleFromZeroConflictMessage
} from "../map/mapUtil";

import {
    getMaxTimeFromPlaybackFile,
    getMinTimeFromPlaybackFile,
    getPlaybackFileValid,
    getPlaybackTimeValueLabelFromMs,
    getAsdFeaturesFromPlaybackFileAndTimestamp,
    getFlightVolumesFromPlaybackFileAndTimestamp
} from "./mapPlaybackUtil";

import useAirspaceLayer from "../hooks/useAirspaceLayer";
import useOpenLayersGeocoder from "../hooks/useGeocoder";
import useOpenLayersMap from "../hooks/useOpenLayersMap";

import { useUserAuth } from "../contexts/authContext";
import { useMap } from "../contexts/mapContext";

const MapPlaybackFileViewer = ({ viewPlaybackFileData, setViewPlaybackFileData }) => {
    const MAP_ID = "playback-map";

    const map = useOpenLayersMap(MAP_ID);

    const fileInputRef = useRef(null);
    const mapWrapperRef = useRef(null);
    const intervalRef = useRef(null);
    const mapLocationRef = useRef(null);
    const hoveredAsdFeatures = useRef([]);

    const asdSourceRef = useRef(null);
    const flightSourceRef = useRef(null);
    const constraintSourceRef = useRef(null);
    const alertVolumeSourceRef = useRef(null);
    const sensorSourceRef = useRef(null);

    const settingsRef = useRef(null);
    const layersRef = useRef(null);

    const { userMapSettings, user, setSnackbar, userProfileSettings, updateProfileSettings } = useUserAuth();
    const { sensors } = useMap();

    const [userMapLayers, setUserMapLayers] = useState(userProfileSettings.layers);
    const [mapContainer, setMapContainer] = useState(null);

    const [playbackTime, setPlaybackTime] = useState(null);

    const [mapLayersOpen, setMapLayersOpen] = useState(false);
    const [mapLayersAnchorEl, setMapLayersAnchorEl] = useState(null);

    const [mapSettingsOpen, setMapSettingsOpen] = useState(false);
    const [mapSettingsAnchorEl, setMapSettingsAnchorEl] = useState(null);

    const [mapTileDropdownOpen, setMapTileDropdownOpen] = useState(false);
    const [mapTileDropdownAnchorEl, setMapTileDropdownAnchorEl] = useState(null);

    const [replaying, setReplaying] = useState(false);

    useAirspaceLayer(map, setUserMapLayers);
    useOpenLayersGeocoder(map);

    useEffect(() => {
        if (!map) return;
        const event = { target: { value: new Date(viewPlaybackFileData.time_start_iso_str).getTime() } };
        handlePlaybackTimeChange(event, viewPlaybackFileData);
    }, [map, viewPlaybackFileData]);

    useEffect(() => {
        if (!map) return;

        let timeout;
        const handlePointerMove = ({ originalEvent, pixel }) => {
            const coordinates = map.getEventCoordinate(originalEvent);

            if (mapLocationRef.current) {
                mapLocationRef.current.innerHTML = `( ${coordinates[1]}, ${coordinates[0]} )`;
            }
            if (userMapSettings.label_visible !== "off") {
                return;
            }
            hoveredAsdFeatures.current.forEach((feature) => {
                feature.set("show_label", false);

                const styleArr = feature.getStyle();
                if (Array.isArray(styleArr)) {
                    const id = feature.getId();
                    styleArr.forEach((style) => {
                        if (style.id === `${id}_label`) {
                            style.setText(undefined);
                        }
                    });
                }
            });
            hoveredAsdFeatures.current = [];

            clearTimeout(timeout);
            timeout = setTimeout(() => {
                map.forEachFeatureAtPixel(pixel, (feature, layer) => {
                    if (!feature.get("asd") || layer !== GetLayer(map, "asd")) {
                        return;
                    }
                    const style = feature.getStyle();
                    if (Array.isArray(style) && style[0].getImage()) {
                        const newStyle = new Style({
                            text: getLabelTextFromFeatureAndUser(feature, user, userMapSettings)
                        });
                        newStyle.id = feature.getId() + "_label";
                        feature.setStyle([...style, newStyle]);

                        feature.set("show_label", true);
                        hoveredAsdFeatures.current.push(feature);
                    }
                });
            }, 250);
        };
        map.on("pointermove", handlePointerMove);

        return () => {
            clearTimeout(timeout);
            map.un("pointermove", handlePointerMove);
        };
    }, [map, userMapSettings]);

    useEffect(() => {
        if (!map) return;

        const layerNames = [
            { name: "asd", visible: userMapLayers.surveillance_sources },
            { name: "flights", visible: userMapLayers.operations },
            { name: "constraints", visible: userMapLayers.constraints },
            { name: "alerts", visible: userMapLayers.alert_volumes },
            { name: "sensors", visible: userMapLayers.radar_footprint }
        ];
        layerNames.forEach(({ name, visible }) => {
            const source = new VectorSource({
                features: []
            });
            if (name === "asd") {
                asdSourceRef.current = source;
            } else if (name === "flights") {
                flightSourceRef.current = source;
            } else if (name === "constraints") {
                constraintSourceRef.current = source;
            } else if (name === "alerts") {
                alertVolumeSourceRef.current = source;
            } else if (name === "sensors") {
                sensorSourceRef.current = source;
            }
            const layer = new VectorLayer({
                source: source,
                zIndex: name === "asd" ? 3 : 2,
                properties: { name: name, selectable: true },
                visible: visible
            });
            map.addLayer(layer);
        });
    }, [map]);

    useEffect(() => {
        const updatedProfileSettings = {
            ...userProfileSettings,
            layers: userMapLayers
        };
        updateProfileSettings(updatedProfileSettings);
    }, [userMapLayers]);

    useEffect(() => {
        if (!map || !sensors || !sensorSourceRef.current) return;
        sensorSourceRef.current.clear();

        sensors.forEach((sensor) => {
            if (isNaN(sensor.lat) || isNaN(sensor.lon) || isNaN(sensor.radius)) {
                return;
            }
            const sensorGeometry = circular([sensor.lon, sensor.lat], sensor.radius, 128);
            const feature = new Feature({ geometry: sensorGeometry, name: sensor.name, sensor, truth_source: sensor.truth_source });
            sensorSourceRef.current.addFeature(feature);

            const sensorStyle = getGeometryStyleFromColor("#000000");
            feature.setStyle(sensorStyle);
        });
    }, [map, sensors]);

    // update features when the map settings change
    useEffect(() => {
        // update base layer brightness
        const baseLayer = GetLayer(map, "base");
        baseLayer?.setOpacity(userMapSettings.brightness || 0);

        // update ASD Tracks
        const asdFeatures = GetFeatures(map, "asd");
        asdFeatures?.forEach((feature) => {
            const id = feature.getId();

            const isFeatureZcLine = id.includes("_") && !id.includes("_pl") && !id.includes("_hl") && !id.includes("_dpz");
            if (isFeatureZcLine) {
                const ownshipId = id.split("_")[0];
                const intruderId = id.split("_")[1];

                const ownship = GetFeature(map, "asd", ownshipId);
                const alerts = ownship.get("zero_confliction_alerts");

                const message = alerts.find((alert) => {
                    return alert.ownship_track.id === ownshipId && alert.intruder_track.id === intruderId;
                });
                const zeroConflictStyle = getZeroConflictionLineStyleFromZeroConflictMessage(message, ownship);
                feature.setStyle(zeroConflictStyle);
            } else {
                const updatedStyle = getAsdFeatureStyleFromLayersAndSettings(feature, userMapLayers, userMapSettings, user);
                feature.setStyle(updatedStyle);

                if (id.includes("_pl")) {
                    const message = feature.get("asd");
                    if (!message) return;

                    const coordinates = getPredLineCoordinatesFromTrackAndSettings(message, userMapSettings);
                    feature.getGeometry().setCoordinates(coordinates);
                }
            }
        });
        // update CMP Volume opacity
        ["flights", "constraints", "alerts"].forEach((volume) => {
            const features = GetFeatures(map, volume);
            features?.forEach((feature) => {
                const style = getGeometryStyleFromFeatureAndSettings(feature, userMapSettings);
                feature.setStyle(style);
            });
        });
        settingsRef.current = userMapSettings;
        layersRef.current = userMapLayers;
    }, [map, userMapSettings, userMapLayers]);

    const handleFileUploadButtonClick = () => {
        if (replaying) {
            alert("Please pause or wait for your current replay to end before uploading a new file.");
        } else {
            fileInputRef.current.click();
        }
    };
    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = async (e) => {
                try {
                    const arrayBuffer = e.target.result;
                    let jsonContent;

                    if (file.name.endsWith(".zip") || file.type === "application/zip") {
                        const zip = await JSZip.loadAsync(arrayBuffer);

                        const jsonFile = Object.values(zip.files).find((f) => {
                            return f.name.endsWith(".json");
                        });
                        if (!jsonFile) {
                            return alert("No JSON file found inside the ZIP archive.");
                        }
                        const unzippedText = await jsonFile.async("string");
                        jsonContent = JSON.parse(unzippedText);
                    } else {
                        const decoder = new TextDecoder("utf-8");
                        const text = decoder.decode(arrayBuffer);

                        jsonContent = JSON.parse(text);
                    }
                    const isValidFormat = getPlaybackFileValid(jsonContent);
                    if (isValidFormat === "") {
                        setViewPlaybackFileData(jsonContent);
                    } else {
                        alert(isValidFormat);
                    }
                } catch (error) {
                    console.error("File processing error:", error);
                    alert("Could not process file. Ensure it is a valid JSON or ZIP file.");
                }
            };
            reader.onerror = (e) => {
                console.error("File reading error:", e.target.error);
            };
            reader.readAsArrayBuffer(file);
        } else {
            alert("Please upload a single file");
        }
    };
    const handlePlaybackTimeChange = (event, data) => {
        if (!data) return;

        const value = event.target.value;
        setPlaybackTime(value);

        // sort each event into its own data structure (to improve runtime)
        const asdMap = new Map();
        const flightMap = new Map();
        const constraintMap = new Map();
        const alertVolumeMap = new Map();
        const zeroConflictAlertMap = new Map();
        const alertVolumeAlertMap = new Map();
        const previousAsdLocationsMap = new Map();

        for (let i = 0; i < data.events.length; i++) {
            const event = data.events[i];
            const eventTimestampMs = new Date(event.timestamp_iso_str).getTime();
            if (eventTimestampMs > value) break;

            if (event.type === "ASD_TRACK") {
                const position = [event.message.lon_deg, event.message.lat_deg];
                const previousLocations = previousAsdLocationsMap.get(event.message.id);

                if (Array.isArray(previousLocations) && previousLocations.length >= 99) {
                    const slicedPositions = [...previousLocations, position].slice(1);
                    previousAsdLocationsMap.set(event.message.id, slicedPositions);
                } else if (Array.isArray(previousLocations)) {
                    previousAsdLocationsMap.set(event.message.id, [...previousLocations, position]);
                } else {
                    previousAsdLocationsMap.set(event.message.id, [position]);
                }
                asdMap.set(event.message.id, event);
            } else if (event.type === "FLIGHT") {
                flightMap.set(event.message.flight_uuid, event);
            } else if (event.type === "CONSTRAINT") {
                constraintMap.set(event.message.constraint_uuid, event);
            } else if (event.type === "ALERT_VOLUME") {
                alertVolumeMap.set(event.message.uuid, event);
            } else if (event.type === "ZERO_CONFLICTION_ALERT") {
                zeroConflictAlertMap.set(event.message.alert_uuid, event);
            } else if (event.type === "ALERT_VOLUME_ALERT") {
                alertVolumeAlertMap.set(event.message.alert_id, event);
            }
        }
        // add ASD tracks, Flights, Constraints, and Alert Volumes for the newly selected timestamp
        const mapLayers = [
            { layer: "ASD_TRACK", source: asdSourceRef.current },
            { layer: "FLIGHT", source: flightSourceRef.current },
            { layer: "CONSTRAINT", source: constraintSourceRef.current },
            { layer: "ALERT_VOLUME", source: alertVolumeSourceRef.current }
        ];
        mapLayers.forEach(({ layer, source }) => {
            if (!source) return;

            let newFeatures = [];
            if (layer === "ASD_TRACK") {
                newFeatures = getAsdFeaturesFromPlaybackFileAndTimestamp(
                    asdMap,
                    value,
                    settingsRef.current,
                    user,
                    layersRef.current,
                    zeroConflictAlertMap,
                    alertVolumeAlertMap,
                    previousAsdLocationsMap
                );
            } else if (layer === "FLIGHT") {
                newFeatures = getFlightVolumesFromPlaybackFileAndTimestamp(flightMap, value, settingsRef.current, layer);
            } else if (layer === "CONSTRAINT") {
                newFeatures = getFlightVolumesFromPlaybackFileAndTimestamp(constraintMap, value, settingsRef.current, layer);
            } else if (layer === "ALERT_VOLUME") {
                newFeatures = getFlightVolumesFromPlaybackFileAndTimestamp(alertVolumeMap, value, settingsRef.current, layer);
            }

            // add or update new features
            newFeatures.forEach((newFeature) => {
                const newFeatureId = newFeature.getId();
                const existingFeature = source.getFeatureById(newFeatureId);

                if (existingFeature) {
                    const newCoordinates = newFeature.getGeometry().getCoordinates();
                    existingFeature.getGeometry().setCoordinates(newCoordinates);

                    const newProperties = newFeature.getProperties();
                    existingFeature.setProperties(newProperties);

                    const newStyle = newFeature.getStyle();
                    existingFeature.setStyle(newStyle);
                } else {
                    source.addFeature(newFeature);
                }
            });

            // remove exired features
            const existingFeatures = source.getFeatures();
            existingFeatures.forEach((existingFeature) => {
                const featureExistsInNewTimeslot = newFeatures.find((newFeature) => {
                    return newFeature.getId() === existingFeature.getId();
                });
                if (featureExistsInNewTimeslot === undefined) {
                    source.removeFeature(existingFeature);
                }
            });
        });
        // create / update zero conflict lines if applicable
        zeroConflictAlertMap.forEach((event) => {
            const message = event.message;
            const zcLineID = `${message.ownship_track.id}_${message.intruder_track.id}`;

            if (message.state === "ACTIVE") {
                const ownshipFeature = GetFeature(map, "asd", message.ownship_track.id);
                const intruderFeature = GetFeature(map, "asd", message.intruder_track.id);

                if (!ownshipFeature || !intruderFeature) {
                    return;
                }
                const zcLineFeature = GetFeature(map, "asd", zcLineID);
                if (zcLineFeature) {
                    const coordinates = getZeroConflictionLineCoordinates(ownshipFeature, intruderFeature);
                    zcLineFeature.getGeometry().setCoordinates(coordinates);

                    const style = getZeroConflictionLineStyleFromZeroConflictMessage(message, ownshipFeature);
                    zcLineFeature.setStyle(style);
                } else {
                    const newZcLineFeature = createZeroConflictionLineFeature(ownshipFeature, intruderFeature, message);
                    asdSourceRef.current.addFeature(newZcLineFeature);
                }
            } else {
                const zcLineFeature = GetFeature(map, "asd", zcLineID);
                if (zcLineFeature) {
                    asdSourceRef.current.removeFeature(zcLineFeature);
                }
            }
        });
    };
    const handleHomeButtonClick = () => {
        if (map && map.getView()) {
            map.getView().setCenter([userMapSettings.longitude, userMapSettings.latitude]);
            map.getView().setZoom(12);
        }
    };
    const handleToggleLayersButtonClick = (e) => {
        setMapLayersAnchorEl(e.currentTarget);
        setMapLayersOpen((prev) => !prev);
    };
    const handleSettingsButtonClick = (e) => {
        setMapSettingsOpen((prev) => !prev);
        setMapSettingsAnchorEl(e.currentTarget);
    };
    const handleMapTileDropdownButtonClick = (e) => {
        setMapTileDropdownOpen((prev) => !prev);
        setMapTileDropdownAnchorEl(e.currentTarget);
    };
    const handleFullScreenButtonClick = () => {
        try {
            if (document.fullscreenElement) {
                document.exitFullscreen();
                return;
            } else if (!mapWrapperRef.current) {
                return;
            }
            mapWrapperRef.current.requestFullscreen();
            if (!mapContainer) {
                setMapContainer(mapWrapperRef.current);
            }
        } catch (err) {
            console.error(err);
        }
    };
    const handleMapLayersChange = (setting, value) => {
        const mapLayers = { ...userMapLayers };
        const toggleSettings = ["surveillance_sources", "operations", "constraints", "alert_volumes", "radar_footprint"];

        if (toggleSettings.includes(setting)) {
            const visibility = !mapLayers[setting];
            GetLayer(map, value)?.setVisible(visibility);
            mapLayers[setting] = visibility;
        } else if (setting === "surveillance_sources_list") {
            mapLayers[setting].push(value);
        } else if (setting === "airspaces") {
            const airspaces = mapLayers[setting];
            if (value) {
                const airspace = airspaces.find((airspace) => airspace.local_type === value);
                airspace.visible = !airspace.visible;
            } else {
                const visibility = !airspaces[0].visible;
                airspaces.forEach((airspace) => (airspace.visible = visibility));
            }
            GetLayer(map, "LAANC_AIRSPACE")?.setStyle((feature) => GetLaancAirspaceStyle(feature, airspaces));
        } else {
            mapLayers[setting] = value;
        }
        setUserMapLayers(mapLayers);
    };
    const handleNarTrackSettingChange = (e) => {
        const mapLayers = {
            ...userMapLayers,
            nar_tracks: e.target.checked
        };
        setUserMapLayers(mapLayers);
    };
    const handlePlayButtonClicked = () => {
        const startTime = playbackTime;
        let i = 1;

        const interval = setInterval(() => {
            const newTimeValueMs = startTime + 1000 * i;
            const maxTimeValueMs = new Date(viewPlaybackFileData.time_end_iso_str).getTime();

            if (newTimeValueMs > maxTimeValueMs) {
                handlePauseButtonClicked();
            } else {
                handlePlaybackTimeChange({ target: { value: newTimeValueMs } }, viewPlaybackFileData);
                i += 1;
            }
        }, 1000);

        intervalRef.current = interval;
        setReplaying(true);
    };
    const handlePauseButtonClicked = () => {
        if (!intervalRef.current) {
            return;
        }
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setReplaying(false);
    };
    return (
        <Box
            ref={mapWrapperRef}
            sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", position: "relative", height: "100%", width: "100%" }}>
                <MapFullScreen
                    map_id={MAP_ID}
                    map={map}
                    buttons={[
                        {
                            id: "fileUpload",
                            name: "Upload file",
                            icon: <FileUploadIcon />,
                            onClick: handleFileUploadButtonClick,
                            visible: true
                        },
                        {
                            id: "viewHome",
                            name: "View Home",
                            visible: true,
                            icon: <HomeIcon />,
                            onClick: handleHomeButtonClick
                        },
                        {
                            id: "toggleLayers",
                            name: "Toggle Layers",
                            visible: true,
                            icon: <LayersIcon />,
                            onClick: handleToggleLayersButtonClick
                        },
                        {
                            id: "editSettings",
                            name: "Edit Settings",
                            visible: true,
                            icon: <SettingsIcon />,
                            onClick: handleSettingsButtonClick
                        },
                        {
                            id: "toggleMapTiles",
                            name: "Map Tiles",
                            visible: true,
                            icon: <MapIcon />,
                            onClick: handleMapTileDropdownButtonClick
                        },
                        {
                            id: "toggleFullscreen",
                            name: document.fullscreenElement ? "Exit Fullscreen" : "Enter Fullscreen",
                            visible: true,
                            icon: document.fullscreenElement ? <CloseIcon /> : <OpenInFullIcon />,
                            onClick: handleFullScreenButtonClick
                        },
                        {
                            id: "close",
                            name: "Close",
                            visible: document.fullscreenElement ? false : true,
                            icon: <CloseIcon />,
                            onClick: () => setViewPlaybackFileData(null)
                        }
                    ]}
                >
                    <MapInfoCard map={map} airspaceSubLayers={userMapLayers.airspaces} mapContainer={mapContainer} />
                </MapFullScreen>

                <Typography
                    ref={mapLocationRef}
                    variant="caption"
                    sx={{ position: "absolute", right: "0.5em", bottom: ".5em", zIndex: 1, textShadow: "0 0 10px #000" }}
                />

                <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    onClick={(e) => (e.target.value = "")}
                    multiple
                    style={{ display: "none" }}
                    accept=".json, .zip, application/json, application/zip"
                />

                {viewPlaybackFileData ? (
                    <Paper
                        sx={{
                            position: "absolute",
                            left: "50%",
                            bottom: "20px",
                            transform: "translateX(-50%)",
                            width: { xs: "75%", md: "50%", xl: "25%" },
                            p: "8px 16px",
                            display: "flex",
                            flexDirection: "row",
                            gap: 2
                        }}
                    >
                        {replaying ? (
                            <IconButton size="small" onClick={handlePauseButtonClicked}>
                                <PauseIcon fontSize="small" />
                            </IconButton>
                        ) : (
                            <IconButton size="small" onClick={handlePlayButtonClicked}>
                                <PlayArrowIcon fontSize="small" />
                            </IconButton>
                        )}
                        <Slider
                            valueLabelDisplay="on"
                            step={1000}
                            value={playbackTime}
                            onChange={(e) => handlePlaybackTimeChange(e, viewPlaybackFileData)}
                            min={getMinTimeFromPlaybackFile(viewPlaybackFileData)}
                            max={getMaxTimeFromPlaybackFile(viewPlaybackFileData)}
                            valueLabelFormat={getPlaybackTimeValueLabelFromMs(playbackTime)}
                            disabled={replaying}
                        />
                    </Paper>
                ) : (
                    <></>
                )}
                {mapSettingsOpen ? (
                    <MapSettingsDropdown
                        mapSettingsOpen={mapSettingsOpen}
                        setMapSettingsOpen={setMapSettingsOpen}
                        mapSettingsAnchorEl={mapSettingsAnchorEl}
                        setSnackbar={setSnackbar}
                        mapContainer={mapContainer}
                    />
                ) : (
                    <></>
                )}
                {mapTileDropdownOpen ? (
                    <MapTileDropdown
                        mapTileDropdownOpen={mapTileDropdownOpen}
                        setMapTileDropdownOpen={setMapTileDropdownOpen}
                        mapTileDropdownAnchorEl={mapTileDropdownAnchorEl}
                        mapContainer={mapContainer}
                    />
                ) : (
                    <></>
                )}
                <MapLayersDropdown
                    mapLayersOpen={mapLayersOpen}
                    setMapLayersOpen={setMapLayersOpen}
                    mapLayersAnchorEl={mapLayersAnchorEl}
                    altitudeLayerRange={userMapLayers.altitude_range}
                    handleAltitudeLayerRangeChanged={(e) => handleMapLayersChange("altitude_range", e.target.value)}
                    narChecked={userMapLayers.nar_tracks}
                    handleNarTrackSettingChange={handleNarTrackSettingChange}
                    sections={[
                        {
                            title: "General",
                            layers: [
                                {
                                    label: "Surveillance Sources",
                                    checked: userMapLayers.surveillance_sources,
                                    handleLayerClick: () => handleMapLayersChange("surveillance_sources", "asd"),
                                    sublayers: userMapLayers.surveillance_sources_list
                                }
                            ]
                        },
                        {
                            title: "Volumes",
                            layers: [
                                {
                                    label: "Airspaces",
                                    checked: userMapLayers.airspaces.every(({ visible }) => visible),
                                    indeterminate: !userMapLayers.airspaces.every(({ visible }) => visible === userMapLayers.airspaces[0].visible),
                                    handleLayerClick: () => handleMapLayersChange("airspaces"),
                                    sublayers: userMapLayers.airspaces.map((airspace) => {
                                        return {
                                            label: airspace.local_type,
                                            checked: airspace.visible,
                                            handleSublayerClick: () => handleMapLayersChange("airspaces", airspace.local_type)
                                        };
                                    })
                                },
                                {
                                    label: "Operations",
                                    checked: userMapLayers.operations,
                                    handleLayerClick: () => handleMapLayersChange("operations", "flights"),
                                    sublayers: []
                                },
                                {
                                    label: "Constraints",
                                    checked: userMapLayers.constraints,
                                    handleLayerClick: () => handleMapLayersChange("constraints", "constraints"),
                                    sublayers: []
                                },
                                {
                                    label: "Alert Volumes",
                                    checked: userMapLayers.alert_volumes,
                                    handleLayerClick: () => handleMapLayersChange("alert_volumes", "alerts"),
                                    sublayers: []
                                },
                                {
                                    label: "Radar Footprint",
                                    checked: userMapLayers.radar_footprint,
                                    handleLayerClick: () => handleMapLayersChange("radar_footprint", "sensors"),
                                    sublayers: []
                                }
                            ]
                        }
                    ]}
                    mapContainer={mapContainer}
                />
            </Box>
        </Box>
    );
};

export default MapPlaybackFileViewer;
